package one.devsbox.www.ui.resources.detail

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.staggeredgrid.LazyVerticalStaggeredGrid
import androidx.compose.foundation.lazy.staggeredgrid.StaggeredGridCells
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import compose.assets.Svg
import compose.assets.svg.*
import kotlinx.datetime.LocalDateTime
import one.devsbox.commons.EMPTY
import one.devsbox.shared.ads.SharedIO
import one.devsbox.shared.files.ResourceType
import one.devsbox.www.io.Repository
import one.devsbox.www.provider.OverlayProvider
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.VideoPlayer
import one.devsbox.www.ui.widgets.*
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import kotlin.math.roundToInt

@Composable
@ComposableNavNode(ArgsId::class)
fun ResourceUI() {
    with(ResourceCtx.get()) {
        ContentResourceUI()
    }
}

@Composable
context(ctx: ResourceCtx)
private fun ContentResourceUI() {
    Box(Modifier.fillMaxSize()) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI(state.nnvl)
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }
    }

    if (ctx.vm.showDeleteConfirmation) {
        AlertDialog(
            onDismissRequest = { ctx.action.dismissDeleteConfirmation() },
            title = { Text(stringResource(Res.string.ui_resources_detail_delete_confirm_title)) },
            text = { Text(stringResource(Res.string.ui_resources_detail_delete_confirm_message)) },
            confirmButton = {
                TextButton(onClick = {
                    ctx.action.confirmDelete()
                }) {
                    Text(
                        text = stringResource(Res.string.ui_resources_detail_delete_btn_confirm),
                        color = MaterialTheme.colorScheme.error
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { ctx.action.dismissDeleteConfirmation() }) {
                    Text(
                        text = stringResource(Res.string.ui_resources_detail_delete_btn_dismiss),
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        )
    }
}

context(ctx: ResourceCtx) @Composable
private fun SuccessUI(media: SharedIO.Media) {
    val type = remember {
        ResourceType[media.type]
    }
    val overlayState = OverlayProvider.OverlayState.rememberState { state ->
        when (type) {
            ResourceType.IMAGE -> {
                Box(
                    modifier = Modifier.fillMaxSize()
                        .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.8f))
                        .clickable { state.hide() }
                ) {
                    LazyImage(
                        id = media.id,
                        modifier = Modifier.fillMaxSize(),
                        contentScale = ContentScale.Fit
                    )
                    FloatingActionButton(
                        onClick = { state.hide() },
                        shape = CircleShape,
                        containerColor = MaterialTheme.colorScheme.surface,
                        contentColor = MaterialTheme.colorScheme.onSurface,
                        modifier = Modifier.align(Alignment.TopEnd)
                            .padding(16.dp)
                    ) {
                        Icon(
                            imageVector = Svg.CloseOutline,
                            contentDescription = "CLOSE",
                        )
                    }
                }
            }

            ResourceType.VIDEO -> {
                VideoPlayer(
                    videoUrl = Repository.createUrlVideo(media.id),
                    isPlaying = false,
                    modifier = Modifier.fillMaxSize(),
                    isFullscreen = true
                ) {
                    state.hide()
                }
            }

            ResourceType.PDF -> Unit
            null -> Unit
        }
    }.apply {
        ctx.overlay.attach(this)
    }

    BoxWithConstraints(
        modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)
    ) {
        val isWide = maxWidth >= 1000.dp

        LazyVerticalStaggeredGrid(
            columns = StaggeredGridCells.Fixed(if (isWide) 2 else 1),
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(
                start = 16.dp,
                top = 16.dp,
                end = 16.dp,
                bottom = 250.dp
            ),
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalItemSpacing = 16.dp
        ) {
            // Preview
            item {
                PreviewSection(media, type, overlayState)
            }

            // Info Section
            item {
                InfoSection(media)
            }

            // Technical Specs
            item {
                TechnicalSpecsSection(media, type)
            }

            // Dominant Palette
            item {
                DominantPaletteSection(media)
            }
        }
    }
}

context(ctx: ResourceCtx) @Composable
private fun PreviewSection(
    media: SharedIO.Media,
    type: ResourceType?,
    overlayState: OverlayProvider.OverlayState,
    modifier: Modifier = Modifier
) {
    Box(
        modifier
            .fillMaxWidth()
            .height(400.dp)
            .clip(RoundedCornerShape(8.dp))
            .border(
                BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
                RoundedCornerShape(8.dp)
            )
            .background(MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.3f)),
        contentAlignment = Alignment.Center
    ) {
        LazyImage(
            id = media.id,
            modifier = Modifier.fillMaxHeight()
                .clickable(media.readyDT != LocalDateTime.EMPTY) {
                    overlayState.show()
                },
            contentScale = ContentScale.FillHeight
        )

        if (media.readyDT != LocalDateTime.EMPTY) when (type) {
            ResourceType.IMAGE -> {
                Icon(
                    imageVector = Svg.OverscanOutline,
                    contentDescription = "FULLSCREEN",
                    tint = MaterialTheme.colorScheme.onSurface,
                    modifier = Modifier.align(Alignment.Center)
                        .background(
                            MaterialTheme.colorScheme.surface.copy(alpha = 0.8f),
                            CircleShape
                        )
                        .padding(16.dp)
                        .size(32.dp)
                )
            }

            ResourceType.VIDEO -> Icon(
                imageVector = Svg.Play,
                contentDescription = "PLAY",
                tint = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier.align(Alignment.Center)
                    .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.8f), CircleShape)
                    .padding(16.dp)
                    .size(32.dp)
            )

            ResourceType.PDF,
            null -> Unit
        }
    }
}

@Composable
private fun InfoSection(media: SharedIO.Media, modifier: Modifier = Modifier) {
    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            LabelSection(stringResource(Res.string.ui_resources_detail_info))

            HorizontalDivider(
                color = MaterialTheme.colorScheme.outlineVariant,
                modifier = Modifier.padding(vertical = 8.dp)
            )
            LabelField(stringResource(Res.string.ui_resources_detail_file_name))
            Text(
                text = media.name,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )


            if (media.tags.isNotEmpty() || media.description.isNotEmpty()) {
                Spacer(Modifier.size(16.dp))
                LabelField(stringResource(Res.string.ui_resources_detail_description))
                Text(
                    text = media.description.ifEmpty { stringResource(Res.string.ui_resources_detail_no_description) },
                    style = MaterialTheme.typography.bodyLarge
                )

                if (media.tags.isNotEmpty()) {
                    Spacer(Modifier.size(16.dp))
                    LabelField(stringResource(Res.string.ui_resources_detail_metadata_tags))
                    FlowRow(
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        media.tags.split(":").forEach { tag ->
                            if (tag.isNotEmpty()) {
                                TagItem(tag)
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun TechnicalSpecsSection(
    media: SharedIO.Media,
    type: ResourceType?,
    modifier: Modifier = Modifier
) {
    Card(
        modifier = modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(containerColor = Color.Transparent),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
            LabelSection(stringResource(Res.string.ui_resources_detail_technical_specs))

            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)

            val optHorizontal =
                stringResource(Res.string.ui_resources_listing_filter_opt_horizontal)
            val optVertical = stringResource(Res.string.ui_resources_listing_filter_opt_vertical)
            SpecItem(
                stringResource(Res.string.ui_resources_detail_orientation),
                if (media.width >= media.height) optHorizontal else optVertical,
                if (media.width >= media.height) Svg.DisplayHorizontalOutline else Svg.DisplayVerticalOutline
            )
            SpecItem(
                stringResource(Res.string.ui_resources_detail_file_type),
                type?.name?.lowercase()?.replaceFirstChar { it.uppercase() }
                    ?: stringResource(Res.string.ui_resources_detail_unknown),
                Svg.MediaOutline
            )
            SpecItem(
                stringResource(Res.string.ui_resources_detail_resolution),
                "${media.width}x${media.height}",
                Svg.OverscanOutline
            )
            if (media.duration > 0) {
                SpecItem(
                    stringResource(Res.string.ui_resources_detail_duration),
                    "${media.duration / 1000}s",
                    Svg.ClockOutline
                )
            }
            SpecItem(
                stringResource(Res.string.ui_resources_detail_aspect_ratio),
                media.aspectRatio,
                Svg.RelativeScaleOutline
            )
            SpecItem(
                stringResource(Res.string.ui_resources_detail_file_size),
                formatFileSize(media.size),
                Svg.DownloadOutline
            )
            SpecItem(
                stringResource(Res.string.ui_resources_detail_date_added),
                media.createDT.date.toString(),
                Svg.CalendarRangeOutline
            )

            val conversionStatus =
                if (media.err.isNotEmpty()) stringResource(Res.string.ui_resources_detail_status_fail)
                else if (media.readyDT == LocalDateTime.EMPTY) stringResource(Res.string.ui_resources_detail_status_processing)
                else stringResource(Res.string.ui_resources_detail_status_ready)
            val conversionColor = when (conversionStatus) {
                stringResource(Res.string.ui_resources_detail_status_ready) -> MaterialTheme.colorScheme.primary
                stringResource(Res.string.ui_resources_detail_status_fail) -> MaterialTheme.colorScheme.error
                else -> MaterialTheme.colorScheme.secondary
            }
            SpecItem(
                stringResource(Res.string.ui_resources_detail_conversion),
                conversionStatus,
                Svg.ChatProcessingOutline,
                valueColor = conversionColor
            )
        }
    }
}

@Composable
private fun DominantPaletteSection(media: SharedIO.Media, modifier: Modifier = Modifier) {
    if (media.colors.isNotEmpty()) {
        Card(
            modifier = modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = Color.Transparent),
            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
        ) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(16.dp)) {
                LabelSection(stringResource(Res.string.ui_resources_detail_dominant_palette))
                HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                Row(
                    Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(4.dp)
                ) {
                    val fallbackColor = MaterialTheme.colorScheme.outlineVariant
                    media.colors.split(":").forEach { hex ->
                        if (hex.isNotEmpty()) {
                            val parsedColor = parseHexColor(hex) ?: fallbackColor
                            Box(
                                Modifier
                                    .weight(1f)
                                    .height(48.dp)
                                    .background(parsedColor)
                            )
                        }
                    }
                }
            }
        }
    }
}


@Composable
private fun TagItem(tag: String) {
    val isApproved = tag.equals("APPROVED", ignoreCase = true)
    val accentColor = MaterialTheme.colorScheme.primary
    Surface(
        color = if (isApproved) accentColor.copy(alpha = 0.1f) else MaterialTheme.colorScheme.surfaceVariant,
        shape = RoundedCornerShape(16.dp),
        border = if (isApproved) BorderStroke(1.dp, accentColor) else null
    ) {
        Text(
            tag.uppercase(),
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 4.dp),
            style = MaterialTheme.typography.labelMedium,
            color = if (isApproved) accentColor else MaterialTheme.colorScheme.onSurface
        )
    }
}

@Composable
private fun SpecItem(
    label: String,
    value: String,
    icon: ImageVector,
    valueColor: Color = MaterialTheme.colorScheme.onSurface
) {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.weight(1f)) {
            Text(
                label,
                style = MaterialTheme.typography.labelMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
            Text(
                value,
                style = MaterialTheme.typography.bodyLarge,
                fontWeight = FontWeight.Bold,
                color = valueColor
            )
        }
        Icon(
            icon,
            null,
            Modifier.size(24.dp),
            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.5f)
        )
    }
}

private fun formatFileSize(size: Long): String {
    val kb = size / 1024.0
    val mb = kb / 1024.0
    return when {
        mb >= 1.0 -> "${(mb * 10).roundToInt() / 10.0} MB"
        kb >= 1.0 -> "${(kb * 10).roundToInt() / 10.0} KB"
        else -> "$size B"
    }
}

private fun parseHexColor(hex: String): Color? {
    return try {
        val h = hex.removePrefix("#")
        if (h.length == 6) Color(
            red = h.substring(0, 2).toInt(16),
            green = h.substring(2, 4).toInt(16),
            blue = h.substring(4, 6).toInt(16)
        ) else null
    } catch (_: Exception) {
        null
    }
}
